import { ReactNode } from 'react';

import { NO_CHIP_MINIMIZER } from '~/core/constants';
import useResetableState from '~/core/hooks/useResetableState';
import { SelectedItemMap, SelectedItemProperties } from '~/core/models';
import { createTypeSafeContext } from '~/core/utils';

export interface ChipMinimizerContextValue {
  selectedChipMinimizers: SelectedItemMap;
  setSelectedChipMinimizers: (value: SelectedItemMap) => void;
  resetSelectedChipMinimizers: () => void;
  restorableChipMinimizers: SelectedItemMap;
  setRestorableChipMinimizers: (value: SelectedItemMap) => void;
  makeRestorableChipMinimizers: () => SelectedItemMap;
  resetRestorableChipMinimizers: () => void;
}

export const [useChipMinimizerContext, ChipMinimizerContext] =
  createTypeSafeContext<ChipMinimizerContextValue>();

export const ChipMinimizerProvider = ({
  children,
}: {
  children?: ReactNode;
}): JSX.Element => {
  const [
    selectedChipMinimizers,
    setSelectedChipMinimizers,
    resetSelectedChipMinimizers,
  ] = useResetableState<Record<number, SelectedItemProperties>>({});
  const [
    restorableChipMinimizers,
    setRestorableChipMinimizers,
    resetRestorableChipMinimizers,
  ] = useResetableState<Record<number, SelectedItemProperties>>({});

  const makeRestorableChipMinimizers = (): Record<
    number,
    SelectedItemProperties
  > =>
    Object.entries(selectedChipMinimizers).reduce<
      Record<number, SelectedItemProperties>
    >((acc, [id, itemProps]) => {
      const chipMinimizerId = parseInt(id);
      const isNoChipMinimizerItem =
        chipMinimizerId === NO_CHIP_MINIMIZER.productId;

      const isSelectedWithQuantity = !!(
        itemProps.isSelected && itemProps.quantity
      );

      if (isNoChipMinimizerItem || isSelectedWithQuantity) {
        acc[chipMinimizerId] = itemProps;
      }

      return acc;
    }, {});

  const chipMinimizerContextValue: ChipMinimizerContextValue = {
    selectedChipMinimizers,
    setSelectedChipMinimizers,
    resetSelectedChipMinimizers,
    restorableChipMinimizers,
    setRestorableChipMinimizers,
    makeRestorableChipMinimizers,
    resetRestorableChipMinimizers,
  };

  return (
    <ChipMinimizerContext.Provider value={chipMinimizerContextValue}>
      {children}
    </ChipMinimizerContext.Provider>
  );
};
